/**
 * Florida ZIP to county.
 *
 * Southwest Florida is covered in detail because that is where nearly every
 * buyer comes from and where the surtax difference actually bites — Lee is
 * 0.5%, Collier is zero, Charlotte is 1%. The rest of the state falls back to
 * a three-digit prefix, which is right for most ZIPs but not all: some
 * prefixes straddle a county line.
 *
 * So this always fills the field as a suggestion and never locks it. The
 * county on the customer record is what drives the tax, and it stays editable.
 *
 * A complete ZIP-to-county table can replace this later. Until then, wrong
 * beats blank only because the person can see and change it.
 */

const EXACT: Record<string, string> = {};
const add = (county: string, zips: string[]) => zips.forEach((z) => (EXACT[z] = county));

// Lee
add("Lee", [
  "33901","33902","33903","33905","33906","33907","33908","33909","33910","33911",
  "33912","33913","33914","33916","33917","33918","33919","33920","33921","33922",
  "33924","33928","33929","33931","33932","33936","33945","33956","33957","33965",
  "33966","33967","33970","33971","33972","33973","33974","33976","33990","33991",
  "33993","33994","34133","34134","34135","34136",
]);
// Collier
add("Collier", [
  "34101","34102","34103","34104","34105","34106","34107","34108","34109","34110",
  "34112","34113","34114","34116","34117","34119","34120","34137","34138","34139",
  "34140","34141","34142","34143","34145","34146",
]);
// Charlotte
add("Charlotte", [
  "33927","33938","33947","33948","33949","33950","33951","33952","33953","33954",
  "33955","33980","33981","33982","33983","34224",
]);
// Hendry, Glades
add("Hendry", ["33440","33935","33975"]);
add("Glades", ["33471"]);
// Sarasota, DeSoto
add("Sarasota", [
  "34223","34228","34229","34230","34231","34232","34233","34234","34235","34236",
  "34237","34238","34239","34240","34241","34242","34243","34275","34276","34277",
  "34278","34285","34286","34287","34288","34289","34292","34293","34295",
]);
add("DeSoto", ["34266","34265","34269"]);

/** Coarse fallback for the rest of the state. Correct for most ZIPs in each
    prefix; a handful of prefixes cross county lines, hence "suggestion". */
const PREFIX: Record<string, string> = {
  "320": "Duval", "321": "Brevard", "322": "Duval", "323": "Leon",
  "324": "Bay", "325": "Escambia", "326": "Alachua", "327": "Orange",
  "328": "Orange", "329": "Brevard", "330": "Broward", "331": "Miami-Dade",
  "332": "Miami-Dade", "333": "Broward", "334": "Palm Beach", "335": "Hillsborough",
  "336": "Hillsborough", "337": "Pinellas", "338": "Polk", "339": "Lee",
  "341": "Collier", "342": "Sarasota", "344": "Marion", "346": "Hernando",
  "347": "Lake", "349": "St. Lucie",
};

export interface ZipMatch { county: string; exact: boolean }

/** Returns null when the ZIP is not Florida-shaped or nothing matches. */
export function countyForZip(zip: string): ZipMatch | null {
  const z = zip.trim().slice(0, 5);
  if (!/^\d{5}$/.test(z)) return null;

  const exact = EXACT[z];
  if (exact) return { county: exact, exact: true };

  const guess = PREFIX[z.slice(0, 3)];
  if (guess) return { county: guess, exact: false };

  return null;
}
